import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  LogIn,
  LogOut,
  Moon,
  Sun,
  ChevronDown,
  User,
  Shield,
  Users,
  Calendar,
  Activity,
  Trophy,
  Award,
  BarChart3,
  ClipboardList,
  Image as ImageIcon,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { supabase } from "../supabaseClient";
import { usePendingAdjustments } from "../hooks/usePendingAdjustments";
import { usePendingRegistrations } from "../hooks/usePendingRegistrations";
import { useClickOutsideAndEscape } from "../hooks/useClickOutsideAndEscape";
import logo from "../assets/images/logo.png";
import { useSeason } from "../context/SeasonContext";
import GlobalSearchBar from "./GlobalSearchBar";

// ── Nav link definitions ───────────────────────────────────────────────────────
const NAV_GROUPS = [
  { type: "link", label: "Home", path: "/" },
  { type: "link", label: "Player Dashboard", path: "/player-dashboard" },
  {
    type: "dropdown",
    label: "Season",
    items: [
      { label: "Standings", path: "/standings", icon: Trophy },
      { label: "Games", path: "/schedule", icon: Calendar },
      { label: "Stats", path: "/stats", icon: BarChart3 },
      {
        label: "Playoff Predictions",
        path: "/playoff-predictions",
        icon: Award,
      },
    ],
  },
  {
    type: "dropdown",
    label: "More",
    items: [
      { label: "Clipboard", path: "/clipboard", icon: ClipboardList },
      { label: "Gallery", path: "/gallery", icon: ImageIcon },
      { label: "Free Agents", path: "/free-agents", icon: Users },
    ],
  },
];

// ── Animated Hamburger → X icon ────────────────────────────────────────────────
const HamburgerIcon = ({ isOpen }) => (
  <div
    className="w-5 h-[14px] flex flex-col justify-between"
    aria-hidden="true"
  >
    <span
      className={`block h-0.5 bg-current rounded-full origin-center transition-all duration-300 ease-in-out ${
        isOpen ? "rotate-45 translate-y-[6px]" : ""
      }`}
    />
    <span
      className={`block h-0.5 bg-current rounded-full transition-all duration-300 ease-in-out ${
        isOpen ? "opacity-0 scale-x-0" : ""
      }`}
    />
    <span
      className={`block h-0.5 bg-current rounded-full origin-center transition-all duration-300 ease-in-out ${
        isOpen ? "-rotate-45 -translate-y-[8px]" : ""
      }`}
    />
  </div>
);

// ── Live Scores pill ───────────────────────────────────────────────────────────
const LiveScoresButton = ({ isLive, onClick }) => {
  if (!isLive) return null;
  return (
    <button
      onClick={onClick}
      className="relative flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase transition-all duration-300 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white shadow-lg shadow-red-500/40 hover:shadow-red-500/60 ring-1 ring-red-400/40 hover:scale-105 active:scale-95"
    >
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
      </span>
      Live Now
    </button>
  );
};

// ── Desktop nav-group dropdown ─────────────────────────────────────────────────
const NavDropdown = ({ label, items, scrolled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();

  useClickOutsideAndEscape(ref, () => setOpen(false));

  const isGroupActive = items.some((item) =>
    location.pathname.startsWith(item.path),
  );

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium tracking-wide transition-all duration-200 ${
          isGroupActive
            ? scrolled
              ? "text-orange-400 bg-orange-500/10"
              : "text-white bg-white/15"
            : scrolled
              ? "text-gray-300 hover:text-white hover:bg-white/[0.08]"
              : "text-white/80 hover:text-white hover:bg-white/10"
        }`}
      >
        {label}
        <ChevronDown
          size={14}
          className={`transition-transform duration-300 ease-in-out ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown panel — CSS-driven visibility so animation plays on close too */}
      <div
        className={`absolute left-0 mt-2.5 w-52 rounded-2xl overflow-hidden shadow-2xl ring-1 z-50 transition-all duration-200 origin-top bg-gray-900/95 backdrop-blur-xl ring-white/10 ${
          open
            ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
            : "opacity-0 scale-95 -translate-y-1 pointer-events-none"
        }`}
      >
        <div className="py-1.5">
          {items.map(({ label: itemLabel, path, icon: Icon }, idx) => (
            <NavLink
              key={path}
              to={path}
              onClick={() => setOpen(false)}
              style={{ transitionDelay: open ? `${idx * 25}ms` : "0ms" }}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-all duration-150 border-l-2 ${
                  isActive
                    ? "bg-orange-500/15 text-orange-400 border-orange-500"
                    : "text-gray-300 hover:bg-white/[0.06] hover:text-white border-transparent"
                }`
              }
            >
              <Icon
                size={15}
                className={`shrink-0 ${
                  location.pathname.startsWith(path)
                    ? "text-orange-400"
                    : "text-gray-500"
                }`}
              />
              {itemLabel}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const { user, signOut, isAdmin, isScorekeeper, isCaptain } = useAuth();
  const pendingCount = usePendingAdjustments(user && isAdmin);
  const pendingRegistrationCount = usePendingRegistrations(user && isAdmin);
  const { isDark, toggleTheme } = useTheme();
  const { registrationSeason } = useSeason();
  const navigate = useNavigate();

  const closeMobile = useCallback(() => setMobileMenuOpen(false), []);

  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);

  useClickOutsideAndEscape(accountRef, () => setAccountOpen(false));

  // ── Scroll-aware navbar ────────────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── Live game indicator ────────────────────────────────────────────────────
  useEffect(() => {
    const checkLive = async () => {
      const { data } = await supabase
        .from("live_games")
        .select("id")
        .eq("status", "live")
        .limit(1);
      setIsLive(!!data?.length);
    };

    checkLive();

    const channel = supabase
      .channel("nav_live_indicator")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "live_games" },
        (payload) => {
          if (payload.new.status === "live") setIsLive(true);
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "live_games" },
        (payload) => {
          if (payload.new.status === "live") setIsLive(true);
          else if (payload.new.status === "final") checkLive();
        },
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  const accountLinks = [
    isAdmin && {
      label: "Schedule Builder",
      path: "/builder",
      icon: Calendar,
    },
    (isScorekeeper || isAdmin) && {
      label: "Score Game",
      path: "/scorer",
      icon: Activity,
    },
    isCaptain && { label: "My Team", path: "/captain", icon: Users },
    isAdmin && {
      label: "Admin",
      path: "/admin",
      icon: Shield,
      adjustmentBadge:
        pendingCount > 0
          ? pendingCount > 9
            ? "9+"
            : String(pendingCount)
          : null,
      adjustmentPath: "/admin?tab=adjustments",
      registrationBadge:
        pendingRegistrationCount > 0
          ? pendingRegistrationCount > 9
            ? "9+"
            : String(pendingRegistrationCount)
          : null,
      registrationPath: "/admin?tab=regs",
    },
  ].filter(Boolean);

  const handleLogout = async () => {
    await signOut();
    navigate("/");
  };

  // ── Desktop link style ─────────────────────────────────────────────────────
  const desktopLinkClass = ({ isActive }) =>
    `px-3 py-1.5 rounded-lg text-sm font-medium tracking-wide transition-all duration-200 ${
      isActive
        ? scrolled
          ? "text-orange-400 bg-orange-500/10"
          : "text-white bg-white/15"
        : scrolled
          ? "text-gray-300 hover:text-white hover:bg-white/[0.08]"
          : "text-white/80 hover:text-white hover:bg-white/10"
    }`;

  // ── Navbar background — transitions from orange to frosted dark on scroll ──
  const navBg = scrolled
    ? "bg-gray-950/90 backdrop-blur-xl border-b border-white/[0.08] shadow-2xl shadow-black/40"
    : "bg-gradient-to-r from-orange-600 via-orange-600 to-orange-700 dark:from-gray-950 dark:via-gray-950 dark:to-gray-900 border-b border-white/10 shadow-lg";

  const navHeight = scrolled ? "h-14" : "h-16";

  return (
    <nav
      className={`sticky top-0 z-50 text-white transition-all duration-300 ease-in-out ${navBg}`}
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* ── Top bar ─────────────────────────────────────────────── */}
        <div
          className={`flex items-center justify-between transition-all duration-300 ${navHeight}`}
        >
          {/* Logo */}
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2.5 focus:outline-none group"
          >
            <img
              src={logo}
              alt="Muqalab Hoops"
              className={`object-contain drop-shadow-lg transition-all duration-300 group-hover:scale-105 ${
                scrolled ? "h-8 w-8" : "h-10 w-10"
              }`}
            />
            <div className="flex flex-col leading-none">
              <span
                className={`font-black tracking-tight transition-all duration-300 ${
                  scrolled ? "text-base" : "text-lg"
                }`}
              >
                Muqalab
              </span>
              <span
                className={`font-semibold tracking-[0.18em] uppercase transition-all duration-300 ${
                  scrolled
                    ? "text-[9px] text-orange-400"
                    : "text-[9px] text-orange-200"
                }`}
              >
                Hoops
              </span>
            </div>
          </button>

          {/* Desktop nav links */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_GROUPS.map((group) =>
              group.type === "link" ? (
                <NavLink
                  key={group.path}
                  to={group.path}
                  className={desktopLinkClass}
                >
                  {group.label}
                </NavLink>
              ) : (
                <NavDropdown
                  key={group.label}
                  label={group.label}
                  items={group.items}
                  scrolled={scrolled}
                />
              ),
            )}
            <div className="ml-3">
              <GlobalSearchBar />
            </div>
          </div>

          {/* Desktop action cluster */}
          <div className="hidden lg:flex items-center gap-2">
            <LiveScoresButton
              isLive={isLive}
              onClick={() => navigate("/live")}
            />

            {registrationSeason && (
              <button
                onClick={() => navigate("/register")}
                className="bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-500 hover:to-emerald-400 px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase transition-all duration-300 shadow-lg shadow-green-500/25 hover:shadow-green-500/40 hover:scale-105 active:scale-95 ring-1 ring-green-400/30"
              >
                Register
              </button>
            )}

            {/* Account dropdown */}
            {user ? (
              <div className="relative" ref={accountRef}>
                <button
                  onClick={() => setAccountOpen((o) => !o)}
                  aria-haspopup="true"
                  aria-expanded={accountOpen}
                  className="relative flex items-center gap-2 bg-white/10 hover:bg-white/15 backdrop-blur-sm border border-white/15 pl-3.5 pr-2.5 py-1.5 rounded-full text-sm font-medium tracking-wide transition-all duration-200 hover:shadow-lg hover:shadow-black/20"
                >
                  <User size={15} className="shrink-0" />
                  <span>Account</span>
                  <ChevronDown
                    size={13}
                    className={`transition-transform duration-300 ${accountOpen ? "rotate-180" : ""}`}
                  />
                  {(pendingCount > 0 || pendingRegistrationCount > 0) && (
                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-orange-600 animate-pulse" />
                  )}
                </button>

                {/* Account panel */}
                <div
                  className={`absolute right-0 mt-2.5 w-52 rounded-2xl overflow-hidden shadow-2xl ring-1 z-50 transition-all duration-200 origin-top-right bg-gray-900/95 backdrop-blur-xl ring-white/10 ${
                    accountOpen
                      ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
                      : "opacity-0 scale-95 -translate-y-1 pointer-events-none"
                  }`}
                >
                  <div className="py-1.5">
                    {accountLinks.map(
                      (
                        {
                          label,
                          path,
                          icon: Icon,
                          adjustmentBadge,
                          adjustmentPath,
                          registrationBadge,
                          registrationPath,
                        },
                        idx,
                      ) => (
                        <button
                          key={path}
                          onClick={() => {
                            navigate(path);
                            setAccountOpen(false);
                          }}
                          style={{
                            transitionDelay: accountOpen
                              ? `${idx * 25}ms`
                              : "0ms",
                          }}
                          className="flex items-center justify-between w-full text-left px-4 py-2.5 text-sm font-medium text-gray-300 hover:bg-white/[0.06] hover:text-white transition-all duration-150 group"
                        >
                          <div className="flex items-center gap-3">
                            <Icon
                              size={15}
                              className="text-orange-400 shrink-0 group-hover:scale-110 transition-transform duration-200"
                            />
                            <span>{label}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {registrationBadge && (
                              <button
                                title="Pending Registrations"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(registrationPath);
                                  setAccountOpen(false);
                                }}
                                className="min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[9px] font-bold text-white bg-red-500 rounded-full hover:bg-red-400 transition-colors"
                              >
                                R{registrationBadge}
                              </button>
                            )}
                            {adjustmentBadge && (
                              <button
                                title="Pending Adjustments"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(adjustmentPath);
                                  setAccountOpen(false);
                                }}
                                className="min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[9px] font-bold text-white bg-blue-500 rounded-full hover:bg-blue-400 transition-colors"
                              >
                                S{adjustmentBadge}
                              </button>
                            )}
                          </div>
                        </button>
                      ),
                    )}

                    {accountLinks.length > 0 && (
                      <div className="border-t border-white/[0.08] my-1" />
                    )}

                    <button
                      onClick={() => {
                        setAccountOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm font-medium text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all duration-150 group"
                    >
                      <LogOut
                        size={15}
                        className="shrink-0 group-hover:scale-110 transition-transform duration-200"
                      />
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={() => navigate("/login")}
                className="flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-medium bg-white/10 hover:bg-white/15 backdrop-blur-sm border border-white/15 transition-all duration-200 hover:shadow-lg"
              >
                <LogIn size={15} />
                Login
              </button>
            )}

            {/* Dark mode toggle */}
            <button
              onClick={toggleTheme}
              aria-label="Toggle dark mode"
              className="p-2 rounded-full bg-white/10 hover:bg-white/15 backdrop-blur-sm border border-white/15 transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95"
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>

          {/* Mobile right cluster */}
          <div className="lg:hidden flex items-center gap-2">
            {isLive && (
              <button
                onClick={() => navigate("/live")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide bg-red-600 text-white shadow-lg shadow-red-500/30"
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                </span>
                Live
              </button>
            )}
            {/* Animated hamburger button */}
            <button
              onClick={() => setMobileMenuOpen((o) => !o)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              className="p-2.5 rounded-xl bg-white/10 hover:bg-white/15 border border-white/15 transition-all duration-200 text-white"
            >
              <HamburgerIcon isOpen={mobileMenuOpen} />
            </button>
          </div>
        </div>

        {/* ── Mobile drawer — max-height animation for smooth open/close ── */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            mobileMenuOpen
              ? "max-h-[700px] opacity-100"
              : "max-h-0 opacity-0 pointer-events-none"
          }`}
        >
          <div className="border-t border-white/10 pt-3 pb-4 space-y-0.5">
            {/* Nav links */}
            {NAV_GROUPS.map((group) =>
              group.type === "link" ? (
                <NavLink
                  key={group.path}
                  to={group.path}
                  onClick={closeMobile}
                  className={({ isActive }) =>
                    `block w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium tracking-wide transition-all duration-200 ${
                      isActive
                        ? "bg-white/15 text-white"
                        : "text-white/75 hover:bg-white/[0.08] hover:text-white"
                    }`
                  }
                >
                  {group.label}
                </NavLink>
              ) : (
                <div key={group.label} className="pt-2">
                  <p className="px-4 pb-1.5 text-[10px] font-black tracking-[0.2em] text-white/40 uppercase">
                    {group.label}
                  </p>
                  {group.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={closeMobile}
                      className={({ isActive }) =>
                        `flex items-center gap-3 w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                          isActive
                            ? "bg-white/15 text-white"
                            : "text-white/75 hover:bg-white/[0.08] hover:text-white"
                        }`
                      }
                    >
                      <item.icon
                        size={15}
                        className="shrink-0 text-orange-400"
                      />
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              ),
            )}

            <div className="border-t border-white/10 my-2" />

            {/* Registration */}
            {registrationSeason && (
              <button
                onClick={() => {
                  navigate("/register");
                  closeMobile();
                }}
                className="w-full text-left px-4 py-2.5 rounded-xl text-sm font-bold tracking-wide bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-500 hover:to-emerald-400 text-white shadow-lg transition-all duration-200"
              >
                Register Now
              </button>
            )}

            {/* Role-gated actions */}
            {(isScorekeeper || isAdmin) && (
              <button
                onClick={() => {
                  navigate("/scorer");
                  closeMobile();
                }}
                className="flex items-center gap-3 w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium text-white/75 hover:bg-white/[0.08] hover:text-white transition-all duration-200"
              >
                <Activity size={15} className="shrink-0 text-orange-400" />
                Score Game
              </button>
            )}

            {user && isCaptain && (
              <button
                onClick={() => {
                  navigate("/captain");
                  closeMobile();
                }}
                className="flex items-center gap-3 w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium text-white/75 hover:bg-white/[0.08] hover:text-white transition-all duration-200"
              >
                <Users size={15} className="shrink-0 text-orange-400" />
                My Team
              </button>
            )}

            {user && isAdmin && (
              <button
                onClick={() => {
                  navigate("/builder");
                  closeMobile();
                }}
                className="flex items-center gap-3 w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium text-white/75 hover:bg-white/[0.08] hover:text-white transition-all duration-200"
              >
                <Calendar size={15} className="shrink-0 text-orange-400" />
                Schedule Builder
              </button>
            )}

            {user && isAdmin && (
              <button
                onClick={() => {
                  navigate("/admin");
                  closeMobile();
                }}
                className="flex items-center justify-between w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium text-white/75 hover:bg-white/[0.08] hover:text-white transition-all duration-200"
              >
                <div className="flex items-center gap-3">
                  <Shield size={15} className="shrink-0 text-orange-400" />
                  <span>Admin</span>
                </div>
                <div className="flex items-center gap-1">
                  {pendingRegistrationCount > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[9px] font-bold text-white bg-red-500 rounded-full">
                      {pendingRegistrationCount > 9
                        ? "R9+"
                        : "R" + pendingRegistrationCount}
                    </span>
                  )}
                  {pendingCount > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[9px] font-bold text-white bg-blue-500 rounded-full">
                      {pendingCount > 9 ? "S9+" : "S" + pendingCount}
                    </span>
                  )}
                </div>
              </button>
            )}

            <div className="px-1 pt-1">
              <GlobalSearchBar />
            </div>

            <div className="border-t border-white/10 my-2" />

            {/* Auth + dark mode row */}
            <div className="flex items-center justify-between px-4 py-1">
              {user ? (
                <button
                  onClick={() => {
                    handleLogout();
                    closeMobile();
                  }}
                  className="flex items-center gap-2 text-sm font-medium text-red-400 hover:text-red-300 transition-colors duration-200"
                >
                  <LogOut size={15} />
                  Logout
                </button>
              ) : (
                <button
                  onClick={() => {
                    navigate("/login");
                    closeMobile();
                  }}
                  className="flex items-center gap-2 text-sm font-medium text-white/75 hover:text-white transition-colors duration-200"
                >
                  <LogIn size={15} />
                  Login
                </button>
              )}

              <button
                onClick={() => {
                  toggleTheme();
                  closeMobile();
                }}
                aria-label="Toggle dark mode"
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/10 hover:bg-white/15 text-white transition-all duration-200"
              >
                {isDark ? (
                  <>
                    <Sun size={15} />
                    Light
                  </>
                ) : (
                  <>
                    <Moon size={15} />
                    Dark
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
